import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useToast } from '@chakra-ui/react';
import { FiThumbsUp, FiThumbsDown, FiLock, FiImage, FiChevronRight, FiCalendar, FiCopy } from 'react-icons/fi';
import { FaDiscord } from 'react-icons/fa';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Navigation, Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

import WebsiteLayout from '../../../layout/website/WebsiteLayout';
import { getTalent, reactTalent } from '../../../../store/talents/talentsSlice';
import { backendUrl } from '../../../../utilities/axios';
import {
  TalentHero, DetailsLayout, DescriptionBox, DetailsSidebar, BackLink,
} from './TalentDetailsStyle';

const CATEGORY_LABEL_KEY = { programmer: 'website.programmers.title', designer: 'website.designers.title', beta_tester: 'website.beta_testers.title' };
const CATEGORY_LIST_PATH = { programmer: '/programmers', designer: '/programmers?tab=designer', beta_tester: '/programmers?tab=beta_tester' };

const TalentDetails = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const toast = useToast({ position: 'top', duration: 3000 });
  const { current: talent, currentLoading } = useSelector(state => state.talents);
  const auth = useSelector(state => state.auth);
  const [optimistic, setOptimistic] = useState(null);
  const userId = auth.user?._id;

  useEffect(() => {
    dispatch(getTalent(id));
  }, [dispatch, id]);

  const images = talent?.images?.length
    ? talent.images.map(img => `${backendUrl}/talents/${img}`)
    : [];

  const myReaction = () => {
    if (!talent) return null;
    if (optimistic !== null) return optimistic;
    if (talent.likedBy?.some(uid => uid === userId)) return 'like';
    if (talent.dislikedBy?.some(uid => uid === userId)) return 'dislike';
    return null;
  };

  const handleReact = (action) => {
    if (!talent) return;
    if (!auth.user) {
      toast({ description: t('website.talents.login_required'), status: 'warning' });
      navigate('/login');
      return;
    }
    const current = myReaction();
    const next = current === action ? null : action;
    setOptimistic(next);
    dispatch(reactTalent({ _id: talent._id, action })).unwrap().catch(() => {
      setOptimistic(null);
      toast({ description: t('general.something_went_wrong'), status: 'error' });
    });
  };

  const handleDiscordClick = () => {
    if (!talent?.discord) return;
    if (/^https?:\/\//i.test(talent.discord)) return;
    navigator.clipboard.writeText(talent.discord);
    toast({ description: t('website.wallet.copied'), status: 'success' });
  };

  if (currentLoading || !talent) {
    return (
      <WebsiteLayout>
        <div style={{ padding: '60px', color: '#fff', textAlign: 'center' }}>
          {t('website.server_details.loading')}
        </div>
      </WebsiteLayout>
    );
  }

  const reaction = myReaction();
  const listPath = CATEGORY_LIST_PATH[talent.category] || '/programmers';
  const isDiscordLink = /^https?:\/\//i.test(talent.discord || '');

  return (
    <WebsiteLayout>
      <TalentHero>
        {images.length > 0 ? (
          <Swiper
            modules={[Autoplay, Navigation, Pagination]}
            loop={images.length > 1}
            autoplay={images.length > 1 ? { delay: 5000, disableOnInteraction: false } : false}
            navigation={images.length > 1}
            pagination={images.length > 1 ? { clickable: true } : false}
            className="talent-swiper"
          >
            {images.map((src, idx) => (
              <SwiperSlide key={idx}>
                <img src={src} alt={`${talent.name} ${idx + 1}`} className="hero-img" />
              </SwiperSlide>
            ))}
          </Swiper>
        ) : (
          <div className="no-image"><FiImage /></div>
        )}
        <div className="hero-overlay" />
        <div className="hero-content">
          <span className="category-badge">{t(CATEGORY_LABEL_KEY[talent.category] || 'website.programmers.title')}</span>
          <div className="talent-name">{talent.name}</div>
        </div>
      </TalentHero>

      <BackLink>
        <Link to={listPath}>
          <FiChevronRight /> {t('website.talents.go_list')}
        </Link>
      </BackLink>

      <DetailsLayout>
        <DescriptionBox>
          <div className="section-title">{t('website.talents.description')}</div>
          <p>{talent.description}</p>
        </DescriptionBox>

        <DetailsSidebar>
          <div className="react-card">
            <button
              className={`react-btn${reaction === 'like' ? ' active-like' : ''}${!auth.user ? ' locked' : ''}`}
              onClick={() => handleReact('like')}
            >
              {!auth.user ? <FiLock /> : <FiThumbsUp />} {talent.likes}
            </button>
            <button
              className={`react-btn${reaction === 'dislike' ? ' active-dislike' : ''}${!auth.user ? ' locked' : ''}`}
              onClick={() => handleReact('dislike')}
            >
              {!auth.user ? <FiLock /> : <FiThumbsDown />} {talent.dislikes}
            </button>
          </div>

          {talent.discord && (
            isDiscordLink ? (
              <a href={talent.discord} target="_blank" rel="noreferrer" className="discord-btn">
                <FaDiscord size={15} /> تواصل
              </a>
            ) : (
              <button type="button" className="discord-btn" onClick={handleDiscordClick}>
                <FaDiscord size={15} /> تواصل <FiCopy size={13} />
              </button>
            )
          )}

          <div className="info-card">
            <div className="info-row">
              <span className="info-label"><FiCalendar /> {t('website.server_details.launch_date')}</span>
              <span className="info-value">{new Date(talent.created_at).toLocaleDateString()}</span>
            </div>
          </div>
        </DetailsSidebar>
      </DetailsLayout>
    </WebsiteLayout>
  );
};

export default TalentDetails;
